import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import {
  ArrowRight,
  CaretLeft,
  CaretRight,
  BellRinging,
  Buildings,
  CalendarCheck,
  Check,
  CheckCircle,
  Eye,
  FileText,
  Sparkle,
  Trophy,
  UserCircle,
  Warning,
  Target,
  XCircle,
  X,
} from '@phosphor-icons/react';

// Feature flag: keep Activity Pulse hidden for VC/incubator accounts for now.
// Set to true later to make it visible for those roles again.
const ACTIVITY_PULSE_SHOW = false;
const notificationStyles = {
  critical: {
    wash: 'from-red-500/[0.065] via-red-500/[0.018] to-transparent',
    chip: 'bg-red-500/10 text-red-600 border-red-500/20 dark:text-red-300',
    accent: 'bg-red-500',
    card: 'border-red-500/15 bg-red-500/[0.025]',
  },
  action: {
    wash: 'from-amber-500/[0.07] via-amber-500/[0.02] to-transparent',
    chip: 'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-300',
    accent: 'bg-amber-500',
    card: 'border-amber-500/15 bg-amber-500/[0.028]',
  },
  success: {
    wash: 'from-emerald-500/[0.06] via-emerald-500/[0.018] to-transparent',
    chip: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-300',
    accent: 'bg-emerald-500',
    card: 'border-emerald-500/15 bg-emerald-500/[0.025]',
  },
  growth: {
    wash: 'from-lime-500/[0.055] via-lime-500/[0.015] to-transparent',
    chip: 'bg-lime-500/10 text-lime-700 border-lime-500/20 dark:text-lime-300',
    accent: 'bg-lime-500',
    card: 'border-lime-500/15 bg-lime-500/[0.022]',
  },
  decline: {
    wash: 'from-rose-500/[0.06] via-rose-500/[0.018] to-transparent',
    chip: 'bg-rose-500/10 text-rose-700 border-rose-500/20 dark:text-rose-300',
    accent: 'bg-rose-500',
    card: 'border-rose-500/15 bg-rose-500/[0.025]',
  },
  investor: {
    wash: 'from-blue-500/[0.055] via-blue-500/[0.015] to-transparent',
    chip: 'bg-blue-500/10 text-blue-700 border-blue-500/20 dark:text-blue-300',
    accent: 'bg-blue-500',
    card: 'border-blue-500/15 bg-blue-500/[0.022]',
  },
  portfolio: {
    wash: 'from-teal-500/[0.055] via-teal-500/[0.015] to-transparent',
    chip: 'bg-teal-500/10 text-teal-700 border-teal-500/20 dark:text-teal-300',
    accent: 'bg-teal-500',
    card: 'border-teal-500/15 bg-teal-500/[0.022]',
  },
  program: {
    wash: 'from-indigo-500/[0.055] via-indigo-500/[0.015] to-transparent',
    chip: 'bg-indigo-500/10 text-indigo-700 border-indigo-500/20 dark:text-indigo-300',
    accent: 'bg-indigo-500',
    card: 'border-indigo-500/15 bg-indigo-500/[0.022]',
  },
  ai: {
    wash: 'from-violet-500/[0.06] via-violet-500/[0.018] to-transparent',
    chip: 'bg-violet-500/10 text-violet-700 border-violet-500/20 dark:text-violet-300',
    accent: 'bg-violet-500',
    card: 'border-violet-500/15 bg-violet-500/[0.024]',
  },
  profile: {
    wash: 'from-orange-500/[0.06] via-orange-500/[0.018] to-transparent',
    chip: 'bg-orange-500/10 text-orange-700 border-orange-500/20 dark:text-orange-300',
    accent: 'bg-orange-500',
    card: 'border-orange-500/15 bg-orange-500/[0.025]',
  },
  documents: {
    wash: 'from-cyan-500/[0.055] via-cyan-500/[0.015] to-transparent',
    chip: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20 dark:text-cyan-300',
    accent: 'bg-cyan-500',
    card: 'border-cyan-500/15 bg-cyan-500/[0.022]',
  },
  team: {
    wash: 'from-pink-500/[0.055] via-pink-500/[0.015] to-transparent',
    chip: 'bg-pink-500/10 text-pink-700 border-pink-500/20 dark:text-pink-300',
    accent: 'bg-pink-500',
    card: 'border-pink-500/15 bg-pink-500/[0.022]',
  },
  finance: {
    wash: 'from-yellow-500/[0.06] via-yellow-500/[0.018] to-transparent',
    chip: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20 dark:text-yellow-300',
    accent: 'bg-yellow-500',
    card: 'border-yellow-500/15 bg-yellow-500/[0.025]',
  },
  info: {
    wash: 'from-slate-500/[0.035] via-transparent to-transparent',
    chip: 'bg-slate-500/10 text-slate-700 border-slate-500/15 dark:text-slate-300',
    accent: 'bg-slate-400',
    card: 'border-slate-500/10 bg-slate-500/[0.016]',
  },
};

const inferNotificationCategory = (item = {}) => {
  if (item.category) return item.category;
  const type = item.type || '';
  if (item.priority === 'critical') return 'critical';
  if (type.includes('profile')) return 'profile';
  if (type.includes('accepted') || type.includes('submitted')) return 'success';
  if (type.includes('investor') || type.includes('visibility')) return 'investor';
  if (type.includes('portfolio')) return type.includes('pending') ? 'action' : 'portfolio';
  if (type.includes('application') || type.includes('program')) return 'program';
  if (type.includes('mrr') || type.includes('growth') || type.includes('momentum')) return 'growth';
  if (type.includes('decline')) return 'decline';
  if (type.includes('cash') || type.includes('burn')) return 'finance';
  if (type.includes('metric_insight')) return 'ai';
  if (type.includes('document') || type.includes('vault')) return 'documents';
  if (type.includes('team') || type.includes('invite')) return 'team';
  if (item.priority === 'high') return 'action';
  return 'info';
};

const visualStyleFor = (item) =>
  notificationStyles[inferNotificationCategory(item)] || notificationStyles.info;

const getImpactSignal = (item = {}) => {
  const combined = `${item.title || ''} ${item.message || ''}`.trim();
  const percentMatch = combined.match(/([+-]?\d+(?:\.\d+)?)\s*%/);
  const currencyMatch = combined.match(/(?:₹|\$|£|€)\s*([\d,.]+(?:\s*[KMB])?)/i);
  const numberMatch = combined.match(/\b([+-]?\d+(?:\.\d+)?(?:\s*[KMB])?)\b/i);
  const title = item.title || 'Update';
  const labelMatch = title.match(/^(.*?)(?:\s+(?:increased|decreased|grew|dropped|rose|fell|up|down|changed)\b)/i);
  const category = inferNotificationCategory(item);

  let value = null;
  if (percentMatch) value = `${percentMatch[1].replace(/^\+/, '')}%`;
  else if (currencyMatch) value = combined.match(/₹/) ? `₹${currencyMatch[1]}` : `${combined.match(/[$£€]/)?.[0] || ''}${currencyMatch[1]}`;
  else if (numberMatch) value = numberMatch[1].replace(/\s+/g, '');

  let fallback = 'info';
  if (['critical', 'decline'].includes(category)) fallback = 'cross';
  else if (['success', 'growth'].includes(category)) fallback = 'check';
  else if (['action', 'program', 'profile', 'finance'].includes(category)) fallback = 'target';
  else if (category === 'ai') fallback = 'sparkle';
  else if (category === 'investor') fallback = 'eye';

  return {
    value,
    fallback,
    label: (labelMatch?.[1] || item.eyebrow || 'Update').trim(),
  };
};

const ImpactFallbackIcon = ({ type }) => {
  if (type === 'cross') return <XCircle size={52} weight="duotone" />;
  if (type === 'check') return <CheckCircle size={52} weight="duotone" />;
  if (type === 'target') return <Target size={52} weight="duotone" />;
  if (type === 'sparkle') return <Sparkle size={48} weight="duotone" />;
  if (type === 'eye') return <Eye size={50} weight="duotone" />;
  return <BellRinging size={48} weight="duotone" />;
};

const getImpactTone = () => ({
  shell: 'activity-pulse-impact-panel',
  value: 'text-teal-300',
  ring: 'border-teal-300/70 shadow-[0_0_26px_rgba(45,212,191,0.24)]',
});

const iconMap = {
  calendar: CalendarCheck,
  trophy: Trophy,
  eye: Eye,
  warning: Warning,
  sparkle: Sparkle,
  check: CheckCircle,
  buildings: Buildings,
  profile: UserCircle,
  file: FileText,
};

const relativeTime = (value) => {
  if (!value) return 'Now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Recently';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'Now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 172800) return 'Yesterday';
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const ActivityIcon = ({ item, size = 16 }) => {
  const Icon = iconMap[item.icon] || BellRinging;
  const styles = visualStyleFor(item);
  return (
    <div className={`activity-pulse-item-icon w-8 h-8 rounded-lg flex items-center justify-center border ${styles.chip} flex-shrink-0`}>
      <Icon size={size} weight={item.icon === 'sparkle' ? 'fill' : 'bold'} />
    </div>
  );
};

const MilestoneStrip = ({ milestones = [] }) => {
  if (!milestones.length) return null;
  return (
    <div className="mt-2 flex max-w-full flex-wrap gap-1 sm:gap-1.5">
      {milestones.map((m) => (
        <span
          key={m.key || m.label}
          className={`inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-semibold sm:px-2 sm:py-1 sm:text-[10px] ${
            m.complete
              ? 'bg-success/15 border-success/30 text-success dark:bg-success/10 dark:border-success/25'
              : 'activity-pulse-milestone-incomplete bg-transparent border-teal-300/15 text-slate-600 dark:bg-transparent dark:border-teal-300/15 dark:text-slate-400'
          }`}
        >
          {m.complete ? <Check size={9} weight="bold" /> : <span className="w-1 h-1 rounded-full bg-slate-400 dark:bg-slate-500" />}
          <span className="min-w-0 break-words">{m.label}</span>
        </span>
      ))}
    </div>
  );
};

// A carousel of compact notifications uses the same footprint as metric tiles.
const CompactPulse = ({ items, onOpen, onDismiss }) => {
  const viewport = useRef(null);
  const [edges, setEdges] = useState({ previous: false, next: false });
  useEffect(() => {
    const node = viewport.current;
    if (!node) return undefined;
    const measure = () => setEdges({ previous: node.scrollLeft > 2, next: node.scrollLeft + node.clientWidth < node.scrollWidth - 2 });
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);
    node.addEventListener('scroll', measure, { passive: true });
    return () => { observer?.disconnect(); node.removeEventListener('scroll', measure); };
  }, [items]);
  const move = (direction) => {
    const node = viewport.current;
    const card = node?.firstElementChild;
    if (!card) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    node.scrollBy({ left: direction * (card.getBoundingClientRect().width + 8), behavior: reduced ? 'auto' : 'smooth' });
  };
  return (
    <section className="fb-pulse-carousel" data-testid="activity-pulse" aria-label="Recent notifications">
      <button type="button" className="fb-pulse-arrow" aria-label="Previous notifications" disabled={!edges.previous} onClick={() => move(-1)}><CaretLeft size={12} weight="bold" /></button>
      <div className="fb-pulse-tiles" data-count={Math.min(items.length, 3)} ref={viewport}>
        {items.map((item) => (
          <article key={item.key} className="fb-pulse-tile" data-unread={!item.read}>
            <div className="fb-pulse-tile-top">
              <ActivityIcon item={item} size={11} />
              <span className="fb-pulse-tile-category">{item.eyebrow || 'Update'}</span>
              <span className="fb-pulse-tile-time">{relativeTime(item.created_at)}</span>
              <button type="button" className="fb-pulse-tile-dismiss" aria-label="Dismiss notification" onClick={(event) => onDismiss(event, item)}><X size={11} weight="bold" /></button>
            </div>
            <h3 title={item.title}>{item.title}</h3>
            <p title={item.message}>{item.message}</p>
            <button type="button" className="fb-pulse-tile-action" onClick={() => onOpen(item)}>{item.action_url ? item.action_label || 'Open' : item.read ? 'Read' : 'Mark as read'}<ArrowRight size={10} weight="bold" /></button>
          </article>
        ))}
      </div>
      <button type="button" className="fb-pulse-arrow" aria-label="Next notifications" disabled={!edges.next} onClick={() => move(1)}><CaretRight size={12} weight="bold" /></button>
    </section>
  );
};

const ActivityPulse = ({ compact = false, className = '', subtle = false, active = true, onUnreadCountChange }) => {
  const navigate = useNavigate();

  let currentUser = {};
  try {
    currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  } catch (err) {
    currentUser = {};
  }
  // Incubators use role='vc' with entity_type='incubator', so role='vc' covers both.
  const hideForVcOrIncubator = currentUser?.role === 'vc' && !ACTIVITY_PULSE_SHOW;
  const [items, setItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionCount, setActionCount] = useState(0);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    onUnreadCountChange?.(items.filter((item) => !item.read && !item.dismissed).length);
  }, [items, onUnreadCountChange]);

  useEffect(() => {
    if (!active) setShowAll(false);
  }, [active]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      const incomingItems = res.data?.items || [];
      setItems(incomingItems);
      setHistoryItems(res.data?.history_items || []);
      setActionCount(res.data?.action_count || 0);
    } catch (err) {
      console.error('Activity Pulse failed to load', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hideForVcOrIncubator) {
      setLoading(false);
      return undefined;
    }

    fetchNotifications();
    const timer = setInterval(fetchNotifications, 60 * 1000);
    return () => clearInterval(timer);
  }, [hideForVcOrIncubator]);

  const hero = items[0];
  const railItems = useMemo(() => items.slice(1), [items]);
  const sevenDayHistory = useMemo(() => {
    const cutoff = Date.now() - (7 * 24 * 60 * 60 * 1000);
    return historyItems.filter((item) => {
      if (!item.created_at) return true;
      const createdAt = new Date(item.created_at).getTime();
      return Number.isNaN(createdAt) || createdAt >= cutoff;
    });
  }, [historyItems]);

  if (hideForVcOrIncubator) return null;

  const openItem = async (item) => {
    setItems((current) => current.map((entry) => entry.key === item.key ? { ...entry, read: true } : entry));
    setHistoryItems((current) => current.map((entry) => entry.key === item.key ? { ...entry, read: true } : entry));
    try { await api.post(`/notifications/${encodeURIComponent(item.key)}/read`); } catch (err) { fetchNotifications(); }
    if (item.action_url) navigate(item.action_url);
  };

  const dismiss = async (e, item) => {
    e.stopPropagation();
    setItems(prev => prev.filter(n => n.key !== item.key));
    setHistoryItems(prev => {
      const dismissedAt = new Date().toISOString();
      const exists = prev.some(n => n.key === item.key);
      if (!exists) {
        return [{ ...item, read: true, dismissed: true, dismissed_at: dismissedAt }, ...prev];
      }
      return prev.map(n =>
        n.key === item.key ? { ...n, read: true, dismissed: true, dismissed_at: dismissedAt } : n
      );
    });

    try {
      await api.post(`/notifications/${encodeURIComponent(item.key)}/dismiss`);
      return true;
    } catch (err) {
      fetchNotifications();
      return false;
    }
  };

  if (loading && subtle) return <div className="fb-pulse-loading" role="status">Loading activity…</div>;

  if (loading) {
    return (
      <div className={`${compact ? 'mb-4 border-y border-border bg-transparent px-1 py-4' : 'mb-5 rounded-xl border border-violet-500/10 bg-white/80 p-4 shadow-[0_8px_28px_rgba(99,102,241,0.06)] backdrop-blur-xl dark:border-violet-300/10 dark:bg-[#191B23]/85'} animate-pulse ${className}`}>
        <div className="h-3 w-28 bg-slate-900/[0.06] dark:bg-white/[0.07] rounded mb-3" />
        <div className="h-6 w-1/2 bg-slate-900/[0.06] dark:bg-white/[0.07] rounded mb-2" />
        <div className="h-3 w-2/5 bg-slate-900/[0.06] dark:bg-white/[0.07] rounded" />
      </div>
    );
  }

  if (!items.length) return subtle ? (
    <section className="fb-pulse-empty rounded-xl border border-border bg-surface text-center" data-testid="activity-pulse-empty">
      <CheckCircle size={26} className="mx-auto text-text-muted" />
      <h3 className="mt-2 text-sm font-semibold text-text-primary">You're all caught up</h3>
      <p className="mt-1 text-xs text-text-secondary">New activity will appear here.</p>
    </section>
  ) : null;

  if (subtle) return <CompactPulse items={items} onOpen={openItem} onDismiss={dismiss} />;

  const impact = getImpactSignal(hero);
  const impactTone = getImpactTone();

  return (
    <>
      <section
        className={`activity-pulse-shell activity-pulse-fixed ${subtle ? 'fb-pulse-subtle' : 'activity-pulse-animated'} ${compact ? 'activity-pulse-compact' : ''} relative overflow-hidden ${compact ? 'mb-4 rounded-2xl border border-border bg-surface text-text-primary shadow-sm' : 'mb-5 rounded-2xl border border-violet-500/15 bg-white/90 text-slate-900 shadow-[0_10px_28px_rgba(15,23,42,0.06)] dark:border-violet-500/20 dark:bg-black/90 dark:text-white'} ${className}`}
        data-testid="activity-pulse"
      >
        <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[150px_minmax(0,1fr)] xl:grid-cols-[190px_minmax(0,1fr)_280px] 2xl:grid-cols-[210px_minmax(0,1fr)_320px]">
          <div className={`relative hidden min-h-[150px] flex-col items-center justify-center border-r p-3 text-center lg:flex xl:p-4 ${impactTone.shell}`}>
            <div className={`activity-pulse-impact-ring flex h-[78px] w-[78px] items-center justify-center rounded-full border-[4px] xl:h-[92px] xl:w-[92px] bg-black/25 text-teal-300 ${impactTone.ring}`}>
              {impact.value ? (
                <span className={`font-heading text-[25px] font-extrabold xl:text-[29px] tracking-[-0.04em] ${impactTone.value}`}>{impact.value}</span>
              ) : (
                <ImpactFallbackIcon type={impact.fallback} />
              )}
            </div>
            <div className="mt-2 max-w-[130px] truncate text-[9px] font-bold uppercase xl:text-[10px] tracking-[0.12em] text-slate-500 dark:text-slate-400" title={impact.label}>
              {impact.label}
            </div>
            <div className="mt-0.5 text-[8px] uppercase xl:text-[9px] tracking-[0.14em] text-slate-400 dark:text-slate-500">Impact</div>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => openItem(hero)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openItem(hero)}
            className="activity-pulse-hero-panel group relative flex min-h-0 cursor-pointer items-center px-3 py-3.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-300/40 sm:px-4 lg:border-r lg:border-teal-300/10 lg:px-4 lg:py-3 xl:px-5"
          >
            <button
              onClick={(e) => dismiss(e, hero)}
              className="absolute right-3 top-3 z-20 rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-900/[0.05] hover:text-slate-700 dark:text-slate-500 dark:hover:bg-white/[0.06] dark:hover:text-slate-200"
              title="Dismiss"
              aria-label="Dismiss notification"
            >
              <X size={14} weight="bold" />
            </button>

            <div className="min-w-0 w-full pr-8">
              <div className="mb-2 flex items-center gap-2">
                <ActivityIcon item={hero} size={15} />
                <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.12em] bg-teal-400/10 text-teal-300 border-teal-300/20`}>
                  {hero.eyebrow || 'Update'}
                </span>
              </div>

              <h3 className="font-heading text-[14px] font-bold leading-snug text-slate-900 dark:text-slate-50 sm:text-[15px] xl:text-[16px]">{hero.title}</h3>
              <p className="mt-1 max-w-3xl text-[11px] leading-[1.45] text-slate-600 dark:text-slate-400 sm:text-[12px] xl:text-[13px]">{hero.message}</p>
              <MilestoneStrip milestones={hero.milestones} />

              {typeof hero.progress === 'number' && (
                <div className="mt-3 flex max-w-lg items-center gap-2.5">
                  <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-900/[0.07] dark:bg-white/[0.08]">
                    <div className="h-full rounded-full bg-teal-400 transition-all duration-700" style={{ width: `${Math.max(3, Math.min(100, hero.progress))}%` }} />
                  </div>
                  <div className="whitespace-nowrap text-xs text-slate-400 dark:text-slate-500">{hero.progress}%</div>
                </div>
              )}

              {hero.action_url && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 transition-colors group-hover:text-teal-700 dark:text-teal-300 dark:group-hover:text-teal-200">
                  {hero.action_label || 'Open'} <ArrowRight size={11} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </div>
              )}
            </div>
          </div>

          <div className="activity-pulse-rail relative hidden min-h-[160px] flex-col overflow-hidden bg-slate-50/55 p-3 dark:bg-white/[0.018] xl:flex xl:col-span-1 xl:min-h-0 xl:border-t-0">
            {!subtle && (
            <div className="mb-2 flex items-center justify-between gap-2 px-1">
              <div>
                <div className="activity-pulse-title text-[11px] font-bold uppercase tracking-[0.16em]">Activity feed</div>
                <div className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">Latest signals and updates</div>
              </div>
              <button
                onClick={() => setShowAll(true)}
                className="activity-pulse-view-all rounded-md border border-teal-300/70 bg-teal-400 px-2.5 py-1 text-[10px] font-bold text-[#071719] shadow-[0_0_14px_rgba(45,212,191,0.18)] transition-all hover:bg-teal-300 hover:shadow-[0_0_18px_rgba(45,212,191,0.3)]"
              >
                View all
              </button>
            </div>

            )}

            <div className="activity-pulse-live-scroll min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain pr-1">
              {railItems.length ? railItems.map(item => (
                <div
                  key={item.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => openItem(item)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openItem(item)}
                  className="activity-pulse-live-item group flex min-h-[48px] w-full cursor-pointer items-start gap-2 rounded-lg border border-slate-900/[0.05] bg-white/65 px-2.5 py-2 text-left transition-all hover:bg-white dark:border-white/[0.05] dark:bg-white/[0.025] dark:hover:bg-white/[0.045]"
                >
                  <div className="activity-pulse-feed-dot mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(45,212,191,0.35)]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="line-clamp-1 text-[12px] font-semibold leading-snug text-slate-800 dark:text-slate-200">{item.title}</div>
                          <span className="activity-pulse-feed-date whitespace-nowrap text-[11px] font-medium text-white/90">{relativeTime(item.created_at)}</span>
                        </div>
                        <div className="mt-0.5 line-clamp-1 text-[10px] text-slate-500 dark:text-slate-500">{item.eyebrow || item.message}</div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => dismiss(e, item)}
                        className="activity-pulse-feed-dismiss -mr-1 -mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-white/45 transition-colors hover:bg-white/[0.07] hover:text-white"
                        title={item.type === 'profile_completion' ? 'Dismiss profile reminder' : 'Dismiss notification'}
                        aria-label={item.type === 'profile_completion' ? 'Dismiss profile completion reminder' : 'Dismiss notification'}
                      >
                        <X size={12} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-border px-3 text-center text-xs text-slate-400 dark:text-slate-500">
                  No additional activity yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {showAll && active && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5" onClick={() => setShowAll(false)}>
          <div className="activity-center-panel w-full sm:max-w-2xl max-h-[82vh] border rounded-t-2xl sm:rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="activity-center-modal-header px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.16em] font-bold text-cyan-300">Activity Center</div>
                <h3 className="activity-center-heading text-lg font-heading font-bold mt-0.5">Everything from the last 7 days</h3>
              </div>
              <button onClick={() => setShowAll(false)} className="activity-center-close p-2 rounded-lg">
                <X size={18} weight="bold" />
              </button>
            </div>
            <div className="activity-center-list overflow-y-auto max-h-[68vh] p-3">
              {sevenDayHistory.length ? sevenDayHistory.map(item => {
                const itemStyles = visualStyleFor(item);
                return (
                  <div key={item.key} role="button" tabIndex={0} onClick={() => openItem(item)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openItem(item)} className={`group w-full text-left p-3.5 rounded-xl border flex items-start gap-3 cursor-pointer transition-colors ${itemStyles.card} hover:brightness-[0.99] dark:hover:brightness-110`}>
                    <ActivityIcon item={item} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[11px] uppercase tracking-[0.10em] font-bold ${itemStyles.chip.split(' ')[1]}`}>{item.eyebrow}</span>
                        <span className="text-xs text-text-muted">{relativeTime(item.created_at)}</span>
                      </div>
                      <div className="font-semibold text-sm text-text-primary mt-1">{item.title}</div>
                      <div className="text-sm text-text-secondary mt-1 leading-relaxed">{item.message}</div>
                      <MilestoneStrip milestones={item.milestones} />
                      <div className="mt-2 flex items-center gap-2 text-xs text-text-muted">
                        {item.dismissed ? (
                          <span className="rounded-full border border-border px-2 py-0.5">Dismissed</span>
                        ) : item.read ? (
                          <span className="rounded-full border border-border px-2 py-0.5">Read</span>
                        ) : (
                          <span className="rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5 text-primary">Unread</span>
                        )}
                        {item.action_url && (
                          <span className="ml-auto inline-flex items-center gap-1 font-semibold text-text-secondary group-hover:text-primary transition-colors">
                            {item.action_label || 'Open'} <ArrowRight size={10} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="px-5 py-10 text-center">
                  <div className="mx-auto w-10 h-10 rounded-xl bg-success/10 text-success border border-success/20 flex items-center justify-center">
                    <CheckCircle size={20} weight="fill" />
                  </div>
                  <div className="mt-3 text-sm font-semibold text-text-primary">You're all caught up</div>
                  <div className="mt-1 text-xs text-text-secondary">No activity from the last 7 days.</div>
                </div>
              )}
            </div>
          </div>
        </div>, document.body
      )}
    </>
  );
};

export default ActivityPulse;
